import { MonthRow, Scenario } from '../../domain/types.js';
import { effectiveRatePeriods } from '../../domain/timeline.js';
import { formatCompactRub, formatMonthLabel, formatRub, formatSignedRub } from '../format.js';
import {
  ChartMode,
  EMPTY_TEXT,
  HAIRLINE_STROKE,
  LegendEntry,
  MODES,
  assignLabelRows,
  createFrame,
  drawXAxis,
  drawYAxis,
  estimateTextWidth,
  marker,
  renderEmpty,
} from './frame.js';
import { linearScale, niceTicks } from './scales.js';
import { areaPath, linePath, svgEl } from './svg.js';
import { TooltipRow, attachCrosshair } from './tooltip.js';

export type BalanceSeries = { name: string; months: MonthRow[]; color: string };

export type BalanceChartInput = {
  months: MonthRow[];
  scenario: Scenario;
  baselineMonths?: MonthRow[];
  /** Comparison scenarios that are neither the current one nor the baseline, in table order. */
  others?: BalanceSeries[];
};

type EventMark = { month: number; text: string };

function clampIndex(value: number, max: number): number {
  return Math.max(0, Math.min(max, value));
}

function collectEvents(scenario: Scenario, count: number): EventMark[] {
  const events: EventMark[] = [];
  scenario.refinances.forEach(refi => events.push({ month: refi.month, text: 'Рефинансирование' }));
  scenario.overpayments
    .filter(op => !op.recurring && !op.adaptive && op.amount > 0)
    .forEach(op => events.push({ month: op.month, text: 'Разовый платёж' }));
  return events.filter(event => event.month >= 0 && event.month < count).sort((a, b) => a.month - b.month);
}

export function render(container: HTMLElement, input: BalanceChartInput, mode: ChartMode = 'compact'): void {
  const { months, scenario } = input;
  const baseline = input.baselineMonths ?? [];
  const hasBaseline = baseline.length > 0;
  const others = input.others ?? [];
  if (!months.length) {
    renderEmpty(container, EMPTY_TEXT);
    return;
  }

  const legend: LegendEntry[] = [];
  if (hasBaseline || others.length > 0) {
    legend.push({ label: 'Этот сценарий', color: 'var(--series-current)', kind: 'area' });
    if (hasBaseline) legend.push({ label: 'Базовый', color: 'var(--series-baseline)', kind: 'dashed' });
    others.forEach(series => legend.push({ label: series.name, color: series.color, kind: 'line' }));
  }
  let maxBalance = scenario.principal;
  months.forEach(m => { maxBalance = Math.max(maxBalance, m.balanceAfter); });
  baseline.forEach(m => { maxBalance = Math.max(maxBalance, m.balanceAfter); });
  others.forEach(series => series.months.forEach(m => { maxBalance = Math.max(maxBalance, m.balanceAfter); }));
  const ticks = niceTicks(0, maxBalance, MODES[mode].yTicks);

  const frame = createFrame(container, mode, {
    ariaLabel: 'Остаток долга',
    legend,
    yLabels: ticks.map(formatCompactRub),
  });
  const { svg, plot, fontSize } = frame;

  const maxLen = Math.max(months.length, baseline.length, ...others.map(series => series.months.length));
  const xScale = linearScale(0, Math.max(1, maxLen - 1), plot.x0, plot.x1);
  const yScale = linearScale(0, ticks[ticks.length - 1], plot.y1, plot.y0);

  /** Balance polyline for a month list, in plot coordinates. */
  const toPoints = (list: MonthRow[]) => list.map((m, i) => ({ x: xScale(i), y: yScale(m.balanceAfter) }));

  drawYAxis(frame, ticks, yScale, formatCompactRub);

  // Discount periods as bands.
  const bands = svgEl('g', { class: 'chart-bands' });
  effectiveRatePeriods(scenario).forEach(period => {
    const start = clampIndex(period.startMonth, maxLen - 1);
    const end = clampIndex(period.endMonth, maxLen - 1);
    if (end < start) return;
    const x = xScale(start);
    bands.append(svgEl('rect', {
      x: x.toFixed(1),
      y: plot.y0.toFixed(1),
      width: Math.max(1, xScale(end) - x).toFixed(1),
      height: (plot.y1 - plot.y0).toFixed(1),
      fill: 'var(--lime)',
      'fill-opacity': 0.08,
    }));
  });
  svg.append(bands);

  const points = toPoints(months);
  const last = points[points.length - 1];

  svg.append(svgEl('path', {
    d: areaPath(points, [{ x: points[0].x, y: plot.y1 }, { x: last.x, y: plot.y1 }]),
    fill: 'var(--series-current)',
    'fill-opacity': 0.12,
    stroke: 'none',
  }));

  // Extra comparison lines sit below the current and baseline series.
  others.forEach((series, index) => {
    svg.append(svgEl('path', {
      d: linePath(toPoints(series.months)),
      fill: 'none',
      stroke: series.color,
      'stroke-width': 1.5,
      'stroke-linejoin': 'round',
      'stroke-linecap': 'round',
      'data-series': 'other',
      'data-index': index,
    }));
  });

  if (hasBaseline) {
    svg.append(svgEl('path', {
      d: linePath(toPoints(baseline)),
      fill: 'none',
      stroke: 'var(--series-baseline)',
      'stroke-width': 2,
      'stroke-dasharray': '6,4',
      'stroke-linejoin': 'round',
      'stroke-linecap': 'round',
      'data-series': 'baseline',
    }));
  }

  svg.append(svgEl('path', {
    d: linePath(points),
    fill: 'none',
    stroke: 'var(--series-current)',
    'stroke-width': 2,
    'stroke-linejoin': 'round',
    'stroke-linecap': 'round',
    'data-series': 'current',
  }));

  // Event hairlines with a marker on the line and a short label.
  const events = collectEvents(scenario, months.length);
  const placements = assignLabelRows(events.map(event => ({ x: xScale(event.month), text: event.text })), fontSize, plot);
  const eventLayer = svgEl('g', { class: 'chart-events' });
  events.forEach((event, index) => {
    const x = xScale(event.month);
    const placement = placements[index];
    eventLayer.append(svgEl('line', {
      x1: x.toFixed(1),
      x2: x.toFixed(1),
      y1: plot.y0.toFixed(1),
      y2: plot.y1.toFixed(1),
      stroke: HAIRLINE_STROKE,
      'stroke-width': 1,
      'stroke-dasharray': '2,3',
    }));
    eventLayer.append(marker(x, points[event.month].y, 'var(--series-current)'));
    eventLayer.append(svgEl('text', {
      x: placement.x.toFixed(1),
      y: (plot.y0 + fontSize + placement.row * (fontSize + 4)).toFixed(1),
      'text-anchor': placement.anchor,
      'font-size': fontSize,
      fill: 'var(--text-muted)',
    }, [event.text]));
  });
  svg.append(eventLayer);

  // Direct end label near the last point, kept inside the plot.
  const endText = `Закрытие: ${formatMonthLabel(months[months.length - 1].dateLabel)}`;
  const endWidth = estimateTextWidth(endText, fontSize);
  const fitsLeft = last.x - 8 - endWidth >= plot.x0;
  const labelY = Math.max(plot.y0 + fontSize, last.y - 10);
  svg.append(svgEl('text', {
    x: (fitsLeft ? last.x - 8 : Math.min(last.x + 8, plot.x1 - endWidth)).toFixed(1),
    y: labelY.toFixed(1),
    'text-anchor': fitsLeft ? 'end' : 'start',
    'font-size': fontSize,
    fill: 'var(--text-primary)',
    class: 'chart-end-label',
  }, [endText]));
  svg.append(marker(last.x, last.y, 'var(--series-current)'));

  // Year labels come from the series that spans the widest x range.
  const longest = [months, baseline, ...others.map(series => series.months)]
    .reduce((best, list) => (list.length > best.length ? list : best), months);
  drawXAxis(frame, longest, { xForIndex: xScale });

  const focusCurrent = marker(0, 0, 'var(--series-current)');
  const focusBaseline = marker(0, 0, 'var(--series-baseline)');
  const focusOthers = others.map(series => marker(0, 0, series.color, 3));
  focusCurrent.setAttribute('visibility', 'hidden');
  focusBaseline.setAttribute('visibility', 'hidden');
  focusOthers.forEach(focus => focus.setAttribute('visibility', 'hidden'));
  svg.append(focusCurrent, focusBaseline, ...focusOthers);

  attachCrosshair({
    svg,
    host: container,
    plot,
    count: maxLen,
    xForIndex: xScale,
    renderTooltip: (i): TooltipRow[] => {
      const month = months[i];
      const base = baseline[i];
      const dated = month ?? base ?? others.map(series => series.months[i]).find(Boolean);
      const rows: TooltipRow[] = [{ value: formatMonthLabel(dated.dateLabel) }];
      const current = month?.balanceAfter ?? 0;
      rows.push({ value: formatRub(current), label: 'Остаток долга', color: 'var(--series-current)' });
      if (hasBaseline) {
        const baseValue = base?.balanceAfter ?? 0;
        rows.push({ value: formatRub(baseValue), label: 'Остаток долга, базовый', color: 'var(--series-baseline)' });
        rows.push({ value: formatSignedRub(current - baseValue), label: 'Разница' });
      }
      // A series that is already paid off shows 0, like the current and baseline rows, so the
      // tooltip keeps its shape while the crosshair moves.
      others.forEach(series => {
        rows.push({ value: formatRub(series.months[i]?.balanceAfter ?? 0), label: series.name, color: series.color });
      });
      return rows;
    },
    onIndex: i => {
      const month = i === null ? undefined : months[i];
      const base = i === null ? undefined : baseline[i];
      if (month && i !== null) {
        focusCurrent.setAttribute('cx', xScale(i).toFixed(1));
        focusCurrent.setAttribute('cy', yScale(month.balanceAfter).toFixed(1));
        focusCurrent.setAttribute('visibility', 'visible');
      } else {
        focusCurrent.setAttribute('visibility', 'hidden');
      }
      if (base && i !== null) {
        focusBaseline.setAttribute('cx', xScale(i).toFixed(1));
        focusBaseline.setAttribute('cy', yScale(base.balanceAfter).toFixed(1));
        focusBaseline.setAttribute('visibility', 'visible');
      } else {
        focusBaseline.setAttribute('visibility', 'hidden');
      }
      focusOthers.forEach((focus, index) => {
        const row = i === null ? undefined : others[index].months[i];
        if (row && i !== null) {
          focus.setAttribute('cx', xScale(i).toFixed(1));
          focus.setAttribute('cy', yScale(row.balanceAfter).toFixed(1));
          focus.setAttribute('visibility', 'visible');
        } else {
          focus.setAttribute('visibility', 'hidden');
        }
      });
    },
  });
}
